import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  FormGroup,
  FormControl,
  TextField,
  Button,
  Divider,
  Grid,
  Alert,
  CircularProgress,
  InputLabel,
  Paper,
  Stack,
  IconButton,
  Tooltip,
  useTheme,
  Select,
  MenuItem
} from '@mui/material';
import { Save as SaveIcon, Info as InfoIcon } from '@mui/icons-material';
import { notificationApi } from 'src/utils/notificationApi';
import { showToast, showErrorToast } from 'src/utils/Toast/SnackbarProvider';
import Page from 'src/components/Page';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';

const NOTIFICATION_TYPES = [
  { name: 'task_assigned', label: 'Task Assigned', description: 'Notify when a task is assigned to you' },
  { name: 'task_due_soon', label: 'Task Due Soon', description: 'Notify when your tasks are due soon' },
  { name: 'task_overdue', label: 'Task Overdue', description: 'Notify when your tasks are overdue' },
  { name: 'task_completed', label: 'Task Completed', description: 'Notify when a task is completed' },
  { name: 'stage_changed', label: 'Stage Changed', description: 'Notify when a company stage is changed' },
  { name: 'company_assigned', label: 'Company Assigned', description: 'Notify when a company is assigned to you' },
  { name: 'company_created', label: 'Company Created', description: 'Notify when a new company is created' },
  { name: 'document_uploaded', label: 'Document Uploaded', description: 'Notify when a document is uploaded' },
  { name: 'meeting_scheduled', label: 'Meeting Scheduled', description: 'Notify when a meeting is scheduled' },
  { name: 'meeting_reminder', label: 'Meeting Reminder', description: 'Remind you of upcoming meetings' },
  { name: 'comment_mention', label: 'Comment Mention', description: 'Notify when you are mentioned in a comment' },
  { name: 'team_member_added', label: 'Team Member Added', description: 'Notify when you are added to a team' },
  { name: 'email_sent', label: 'Email Sent', description: 'Notify when an email is sent through the system' },
  { name: 'ticket_status_updates', label: 'Ticket Status Updates', description: 'Notify when a support ticket status is updated' },
  { name: 'ticket_comment_replies', label: 'Ticket Comment Replies', description: 'Notify when a comment is added to your ticket' },
];

export default function NotificationSettingsPage() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    // Global settings
    push_enabled: true,
    email_enabled: true,
    in_app_enabled: true,
    
    // Quiet hours
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00:00', // HH:MM:SS format
    quiet_hours_end: '07:00:00',   // HH:MM:SS format
    
    // Per-notification type settings
    task_assigned: true,
    task_due_soon: true,
    task_overdue: true,
    task_completed: true,
    stage_changed: true,
    company_assigned: true,
    company_created: true,
    document_uploaded: false,
    meeting_scheduled: true,
    meeting_reminder: true,
    comment_mention: true,
    team_member_added: true,
    email_sent: false,
    ticket_status_updates: true,
    ticket_comment_replies: true,
  });

  // Load settings from backend
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await notificationApi.getSettings();
        
        // Map API response to local state
        const apiSettings = response.data || response;
        
        setSettings({
          push_enabled: apiSettings.push_enabled,
          email_enabled: apiSettings.email_enabled,
          in_app_enabled: apiSettings.in_app_enabled,
          
          quiet_hours_enabled: apiSettings.quiet_hours_enabled,
          quiet_hours_start: apiSettings.quiet_hours_start || '22:00:00',
          quiet_hours_end: apiSettings.quiet_hours_end || '07:00:00',
          
          task_assigned: apiSettings.task_assigned,
          task_due_soon: apiSettings.task_due_soon,
          task_overdue: apiSettings.task_overdue,
          task_completed: apiSettings.task_completed,
          stage_changed: apiSettings.stage_changed,
          company_assigned: apiSettings.company_assigned,
          company_created: apiSettings.company_created,
          document_uploaded: apiSettings.document_uploaded,
          meeting_scheduled: apiSettings.meeting_scheduled,
          meeting_reminder: apiSettings.meeting_reminder,
          comment_mention: apiSettings.comment_mention,
          team_member_added: apiSettings.team_member_added,
          email_sent: apiSettings.email_sent,
          ticket_status_updates: apiSettings.ticket_status_updates,
          ticket_comment_replies: apiSettings.ticket_comment_replies,
        });
      } catch (error) {
        console.error('Error fetching notification settings:', error);
        showErrorToast('Failed to load notification settings');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, []);
  
  // Handle changes to settings
  const handleToggle = (setting) => (event) => {
    setSettings({
      ...settings,
      [setting]: event.target.checked,
    });
  };
  
  // Handle changes to quiet hours
  const handleQuietHoursChange = (field) => (event) => {
    setSettings({
      ...settings,
      [field]: event.target.value,
    });
  };
  
  // Save settings to backend
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      
      await notificationApi.updateSettings(settings);
      
      showToast('Notification settings saved', 'success');
    } catch (error) {
      console.error('Error saving notification settings:', error);
      showErrorToast('Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Page title="Notification Settings">
      <Box sx={{ p: 3 }}>
        <HeaderBreadcrumbs
          heading="Notification Settings"
          links={[
            { name: 'Dashboard', href: '/dashboard' },
            { name: 'Settings', href: '/settings' },
            { name: 'Notifications' }
          ]}
          action={
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          }
        />
        
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            {/* Global Settings */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Global Settings" />
                <CardContent>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.in_app_enabled}
                          onChange={handleToggle('in_app_enabled')}
                          name="in_app_enabled"
                          color="primary"
                        />
                      }
                      label="In-app Notifications"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.push_enabled}
                          onChange={handleToggle('push_enabled')}
                          name="push_enabled"
                          color="primary"
                        />
                      }
                      label="Push Notifications"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.email_enabled}
                          onChange={handleToggle('email_enabled')}
                          name="email_enabled"
                          color="primary"
                        />
                      }
                      label="Email Notifications"
                    />
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                      Email notifications contain minimal information and direct you to the portal for details
                    </Typography>
                  </FormGroup>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Quiet Hours Settings */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader 
                  title="Quiet Hours" 
                  action={
                    <Tooltip title="During quiet hours, notifications won't disturb you but will still be available in your notification center">
                      <IconButton>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  }
                />
                <CardContent>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.quiet_hours_enabled}
                          onChange={handleToggle('quiet_hours_enabled')}
                          name="quiet_hours_enabled"
                          color="primary"
                        />
                      }
                      label="Enable Quiet Hours"
                    />
                  </FormGroup>
                  
                  {settings.quiet_hours_enabled && (
                    <Box sx={{ mt: 3 }}>
                      <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} sm={5}>
                          <FormControl fullWidth>
                            <InputLabel>Start Time</InputLabel>
                            <Select
                              value={settings.quiet_hours_start || '22:00:00'}
                              onChange={handleQuietHoursChange('quiet_hours_start')}
                              label="Start Time"
                            >
                              {Array.from({ length: 24 }, (_, hour) => (
                                <MenuItem key={hour} value={`${hour.toString().padStart(2, '0')}:00:00`}>
                                  {hour === 0 ? '12:00 AM' : 
                                   hour < 12 ? `${hour}:00 AM` : 
                                   hour === 12 ? '12:00 PM' : 
                                   `${hour - 12}:00 PM`}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
                          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600 }}>
                            to
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} sm={5}>
                          <FormControl fullWidth>
                            <InputLabel>End Time</InputLabel>
                            <Select
                              value={settings.quiet_hours_end || '07:00:00'}
                              onChange={handleQuietHoursChange('quiet_hours_end')}
                              label="End Time"
                            >
                              {Array.from({ length: 24 }, (_, hour) => (
                                <MenuItem key={hour} value={`${hour.toString().padStart(2, '0')}:00:00`}>
                                  {hour === 0 ? '12:00 AM' : 
                                   hour < 12 ? `${hour}:00 AM` : 
                                   hour === 12 ? '12:00 PM' : 
                                   `${hour - 12}:00 PM`}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                      
                      <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 3 }}>
                        During this time period, no push notifications will be sent, but you'll still receive them in-app.
                      </Alert>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            {/* Notification Types */}
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Notification Types" />
                <Divider />
                <CardContent>
                  <Typography variant="body2" sx={{ mb: 3 }}>
                    Enable or disable specific types of notifications
                  </Typography>
                  
                  <Grid container spacing={3}>
                    {NOTIFICATION_TYPES.map((type) => (
                      <Grid item xs={12} sm={6} md={4} key={type.name}>
                        <Paper 
                          variant="outlined" 
                          sx={{ 
                            p: 2,
                            backgroundColor: settings[type.name] ? 'action.selected' : 'background.paper',
                            borderColor: settings[type.name] ? 'primary.light' : 'divider'
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={settings[type.name]}
                                onChange={handleToggle(type.name)}
                                name={type.name}
                                color="primary"
                              />
                            }
                            label={<Typography variant="subtitle2">{type.label}</Typography>}
                            sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {type.description}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Page>
  );
}
